import React, { useEffect, useState } from "react";
import {
  UserOutlined,
  GlobalOutlined,
  SettingOutlined,
  PieChartOutlined,
  SnippetsOutlined,
  PlusOutlined,
  FileProtectOutlined,
  ScheduleOutlined,
} from "@ant-design/icons";
import { Avatar, Popover, Image, ConfigProvider } from "antd";
import { Layout, Menu } from "antd";
import { useNavigate } from "react-router-dom";
import TabbedWorkspace from "../tabs/TabbedWorkspace";
import accountsApi from "../api/Accounts/AccountsApi";
import MenuItem from "antd/es/menu/MenuItem";
import { ApiService, Template } from "../api";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import type { MenuProps } from "antd";
import { APP_BG } from "../theme/antdTheme";
import MyProfileModal from "../components/MyProfileModal";

const { Header, Content, Footer, Sider } = Layout;

// --- Sider styling (only used in this layout) ---
const SIDER_BG = "#0C2E5C"; // dark navy sidebar
const SIDER_SELECTED = "#1668dc"; // selected menu item highlight
const SIDER_WIDTH = 280; // wider sidebar

type MenuItem = Required<MenuProps>["items"][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  onClick?: () => void,
  children?: MenuItem[],
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    onClick,
  } as MenuItem;
}

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [profileOpen, setProfileOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const userInfo = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo") || "{}")
    : null;

  const systemMenuItems: MenuItem[] = [
    getItem(t("New"), "new", <PlusOutlined />, () => {
      navigate("/");
    }),
    getItem(t("Analytics"), "analytics", <PieChartOutlined />, () => {
      navigate("/analytics");
    }),
    getItem(t("My Requests"), "my_requests", <SnippetsOutlined />, () => {
      navigate("/my-requests");
    }),
    getItem(t("My Approvals"), "my_approvals", <FileProtectOutlined />, () => {
      navigate("/my-approvals");
    }),
  ];

  const isAdmin = Boolean(
    userInfo?.isAdmin ?? userInfo?.is_staff ?? userInfo?.is_superuser,
  );

  const administrationMenuItems: MenuItem[] = [
    getItem(t("Works Plan"), "works_plan", <ScheduleOutlined />, () => {
      navigate("/works-plan");
    }),
    // Template management is an admin-only area.
    // ...(isAdmin
    //   ? [
    //       getItem(t("Settings"), "settings", <SettingOutlined />, undefined, [
    //         getItem(t("Templates"), "templates", null, () => {
    //           navigate("/templates");
    //         }),
    //       ]),
    //     ]
    //   : []),
    getItem(t("Languages"), "languages", <GlobalOutlined />, undefined, [
      getItem(t("English"), "en", null, () => {
        handleChangeLanguage("en");
      }),
      getItem(t("Kazakh"), "kk", null, () => {
        handleChangeLanguage("kk");
      }),
      getItem(t("Russian"), "ru", null, () => {
        handleChangeLanguage("ru");
      }),
      getItem(t("Chinese"), "zh", null, () => {
        handleChangeLanguage("zh");
      }),
    ]),
  ];

  useEffect(() => {
    setItems([...systemMenuItems, ...administrationMenuItems]);
  }, []);

  const handleChangeLanguage = (language: string) => {
    i18n.changeLanguage(language);
    localStorage.setItem("language", language);
    window.location.reload();
  };

  function handleLogout(): void {
    accountsApi.logout();
    navigate("/login");
  }

  const checkUserToken = () => {
    if (!userInfo || !userInfo.access) {
      setIsLoggedIn(false);
      navigate("/login");
      return;
    }
    setIsLoggedIn(true);
  };

  const loadTemplateTypes = async () => {
    const templateTypes = await ApiService.apiMainTemplateTypesList("admin");
    const userMenuItems: MenuItem[] = templateTypes.map((tt, index) =>
      getItem(
        t(tt.name),
        `${tt.id}_${index}`,
        <SnippetsOutlined />,
        undefined,
        tt.filtered_templates.map((p: Template, index) =>
          getItem(t(p.name), `${p.short_name}_${index}}`, null, () => {
            navigate(`/requests/${tt.short_name}/${p.short_name}`);
          }),
        ),
      ),
    );
    setItems([
      ...systemMenuItems,
      ...userMenuItems,
      ...administrationMenuItems,
    ]);
  };

  useEffect(() => {
    checkUserToken(); // eslint-disable-next-line react-hooks/exhaustive-deps
    loadTemplateTypes();
  }, [isLoggedIn]);

  useEffect(() => {
    const storedLanguage = localStorage.getItem("language");
    if (storedLanguage) {
      i18n.changeLanguage(storedLanguage);
    }
  }, [i18n]);

  const text = userInfo ? userInfo.name : "User";
  const content = (
    <Menu selectable={false}>
      <MenuItem
        key="profile"
        onClick={() => {
          setPopoverOpen(false);
          setProfileOpen(true);
        }}
      >
        {t("My Profile")}
      </MenuItem>
      <MenuItem
        key="logout"
        onClick={() => {
          setPopoverOpen(false);
          handleLogout();
        }}
      >
        {t("Logout")}
      </MenuItem>
    </Menu>
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Sider theme is scoped here so its dark-navy tokens don't leak
          into the rest of the app (which uses the global antdTheme). */}
      <ConfigProvider
        theme={{
          components: {
            Menu: {
              darkItemBg: SIDER_BG,
              darkSubMenuItemBg: SIDER_BG,
              darkItemSelectedBg: SIDER_SELECTED,
              // Brighter white menu text + clear hover highlight
              darkItemColor: "#ffffff",
              darkItemHoverColor: "#ffffff",
              darkItemHoverBg: "rgba(255,255,255,0.12)",
            },
            Layout: {
              siderBg: SIDER_BG,
              triggerBg: SIDER_BG,
            },
          },
        }}
      >
        <Sider
          breakpoint="lg"
          collapsible
          theme="dark"
          width={SIDER_WIDTH}
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          style={{ backgroundColor: SIDER_BG }}
        >
          <div style={{ textAlign: "center", marginTop: "1em" }}>
            <Image
              src="/logo.png"
              width={collapsed ? 35 : 105}
              preview={false}
            ></Image>
          </div>

          <h3
            style={{
              textAlign: "center",
              color: "#ffffff",
              fontSize: 20,
              fontWeight: 700,
              margin: "0.5em 0 1em",
            }}
          >
            {/* Product name — intentionally not translated. */}
            {collapsed ? "SWS" : "Safe Work System"}
          </h3>
          <Menu
            mode="inline"
            theme="dark"
            items={items}
            style={{ backgroundColor: SIDER_BG }}
          />
        </Sider>
      </ConfigProvider>

      <Layout>
        <Header
          style={{
            padding: "0 24px",
            backgroundColor: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 600, color: "#33475B" }}>
            {t("Welcome")}, {text}
          </span>
          <Popover
            placement="bottomRight"
            title={text}
            content={content}
            trigger="click"
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
          >
            <Avatar
              style={{ cursor: "pointer" }}
              shape="square"
              size={40}
              icon={<UserOutlined />}
            />
          </Popover>
        </Header>
        <Content style={{ backgroundColor: APP_BG }}>
          <div
            style={{
              margin: "16px 16px 24px",
              minHeight: 280,
            }}
          >
            {isLoggedIn ? <TabbedWorkspace /> : null}
          </div>
        </Content>
        <Footer style={{ textAlign: "center", backgroundColor: APP_BG }}>
          {t("Intellectual Property of Smart Solutions")}
        </Footer>
      </Layout>
      <MyProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
      />
    </Layout>
  );
};

export default MainLayout;
